import { useContext, useEffect, useState } from "react";
import UserStep from "./steps/userStep";
import { getActiveStores } from "../functions";
import ToastContext from "../../../context/toastProvider";
import { cashnator, cashnatorFloat, cpfMask, phoneMask } from "../../utils/functions";
import DeliveryStep from "./steps/deliveryStep";
import ProductStep from "./steps/productStep";
import ShippingStep from "./steps/shippingStep";
import CheckoutStep from "./steps/checkoutStep";
import SummaryStep from "./steps/summaryStep";
import { useLocation, useNavigate } from "react-router-dom";
import { getGradingTypeLabel, getPreLacreFormatLabel, normalizeGradingType, priorityTypes } from "../../utils/gradingTypes";
import WizardStepper from "./WizardStepper";
import { WizardSection, DoneCard } from "./WizardSection";
import NotesPanel from "./NotesPanel";

const DELIVERY_LABELS = { correios: "Correios", presencial: "Presencial" };

const CreateProduct = ({ user }) => {

    const { badToast } = useContext(ToastContext);
    const location = useLocation();
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [userData, setUserData] = useState({});
    const [deliveryData, setDeliveryData] = useState({});
    const [productData, setProductData] = useState({});

    const [stores, setStores] = useState([]);

    useEffect(() => {
        doGetStores();
        if (location?.state?.origin === "users-new") {
            setUserData(location?.state?.user);
            setStep(1);
        }
        // eslint-disable-next-line
    }, [])

    const doGetStores = async (attempt = 0) => {
        if (!stores.length) {
            const data = await getActiveStores();
            if (data.type === "success") {
                setStores(data.data);
                return null;
            }
            // Falha transitória (ex.: cold start pós-deploy): tenta de novo antes de avisar.
            if (attempt < 1) {
                return doGetStores(attempt + 1);
            }
            badToast("Erro ao carregar as lojas — a entrega presencial pode ficar indisponível. Recarregue a página para tentar de novo.");
        }
    }

    const refresh = (id) => {
        navigate(`/manafix/products/${id}`);
    }

    // Etapas visíveis (índices internos preservados; 3 = frete só nos Correios).
    // Status e comentários saíram do fluxo linear: status é opção secundária na
    // revisão; comentários viram o painel de anotações disponível o tempo todo.
    const stepperSteps = [
        { key: 0, label: "Cliente" },
        { key: 1, label: "Entrega" },
        { key: 2, label: "Pedido" },
        ...(deliveryData?.delivery === "correios" ? [{ key: 3, label: "Frete" }] : []),
        { key: 4, label: "Valores" },
        { key: 7, label: "Revisão" },
    ];

    const header = (
        <>
            <h1 className="mb-3 text-center text-2xl font-bold text-laranjinha">Novo pedido</h1>
            <WizardStepper steps={stepperSteps} currentStep={step} onStepClick={setStep} />
        </>
    );

    const notes = <NotesPanel productData={productData} setProductData={setProductData} />;

    if (step === 7) {
        return (
            <main className="mx-auto mb-10 w-full max-w-3xl px-4 pt-4 text-center">
                {header}
                <SummaryStep stores={stores} user={user} productData={productData} setProductData={setProductData} deliveryData={deliveryData} setStep={setStep} refresh={refresh} userData={userData} />
                {notes}
            </main>
        )
    }

    return (
        <main className="mx-auto mb-10 w-full max-w-3xl px-4 pt-4 text-center">
            {header}

            {
                step === 0 ?
                    <WizardSection title="Cliente">
                        <UserStep user={user} setUserData={setUserData} setStep={setStep} />
                    </WizardSection>
                    :
                    <DoneCard
                        title={`Cliente — ${userData?.name ?? ""}`}
                        onEdit={() => setStep(0)}
                        rows={[
                            { label: "E-mail", value: userData?.email },
                            { label: "Telefone", value: phoneMask(userData?.phone) },
                            {
                                label: userData?.documentType === "other" ? "Documento" : userData?.documentType,
                                value: userData?.documentType === "other" ? userData?.documentNumber : cpfMask(userData?.documentNumber),
                            },
                        ]}
                    />
            }

            {
                step === 1 ?
                    <WizardSection title="Entrega">
                        <DeliveryStep user={user} userData={userData} setStep={setStep} setDeliveryData={setDeliveryData} stores={stores} deliveryData={deliveryData} />
                    </WizardSection>
                    : step > 1 ?
                        <DoneCard
                            title={`Entrega — ${DELIVERY_LABELS[deliveryData?.delivery] ?? deliveryData?.delivery ?? ""}`}
                            onEdit={() => setStep(1)}
                            rows={
                                deliveryData?.delivery === "correios" ? [
                                    { label: "Recebedor", value: deliveryData?.address?.name },
                                    {
                                        label: "Endereço",
                                        value: `${deliveryData?.address?.street ?? ""}, ${deliveryData?.address?.houseNumber ?? ""} — ${deliveryData?.address?.city ?? ""}/${deliveryData?.address?.state ?? ""}`,
                                    },
                                    { label: "CEP", value: deliveryData?.address?.postalCode },
                                ] : [
                                    { label: "Loja", value: stores?.filter(s => s?._id === deliveryData?.store)[0]?.name },
                                ]
                            }
                        />
                        : null
            }

            {
                step === 2 ?
                    <WizardSection title="Dados do pedido">
                        <ProductStep setStep={setStep} deliveryData={deliveryData} setProductData={setProductData} productData={productData} />
                    </WizardSection>
                    : step > 2 ?
                        <DoneCard
                            title={`Pedido — ${getGradingTypeLabel(productData?.gradingType)}`}
                            onEdit={() => setStep(2)}
                            rows={[
                                { label: "Nº itens", value: productData?.numberOfCards },
                                { label: "Autógrafo", value: productData?.autographEvaluation ? "Sim" : "Não" },
                                normalizeGradingType(productData?.gradingType) === "pre" && productData?.preLacreFormat
                                    ? { label: "Pré lacre", value: getPreLacreFormatLabel(productData?.preLacreFormat) }
                                    : null,
                                { label: "Prioridade", value: priorityTypes[productData?.priority] },
                                { label: "Valor declarado", value: cashnator(productData?.declaredValue) },
                            ]}
                        />
                        : null
            }

            {
                deliveryData?.delivery === "correios" ?
                    step === 3 ?
                        <WizardSection title="Frete">
                            <ShippingStep productData={productData} setProductData={setProductData} deliveryData={deliveryData} setStep={setStep} setDeliveryData={setDeliveryData} />
                        </WizardSection>
                        : step > 3 ?
                            <DoneCard
                                title="Frete"
                                onEdit={() => setStep(3)}
                                rows={[
                                    { label: "Serviço", value: deliveryData?.shipping?.service },
                                    { label: "Valor", value: cashnatorFloat(deliveryData?.shipping?.price) },
                                ]}
                            />
                            : null
                    : null
            }

            {
                step === 4 ?
                    <WizardSection title="Valores e prazo">
                        <CheckoutStep user={user} productData={productData} deliveryData={deliveryData} setDeliveryData={setDeliveryData} setStep={setStep} setProductData={setProductData} />
                    </WizardSection>
                    : step > 4 ?
                        <DoneCard
                            title="Valores"
                            onEdit={() => setStep(4)}
                            rows={[
                                { label: "Taxa de manuseio", value: cashnator(productData?.insurance) },
                                { label: "Valor final", value: cashnator(productData?.finalPrice) },
                                { label: "Prazo", value: productData?.deadline ? `${productData.deadline} dias úteis` : null },
                            ]}
                        />
                        : null
            }

            {notes}
        </main>
    )
}

export default CreateProduct;
